import { useTranslation } from 'react-i18next';
import type { OverrideProp, OverrideViewport } from '@evo-hub/shared';
import { Input } from '../../ui/Input.js';
import { Select } from '../../ui/Select.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { Pill } from '../../PillGroup.js';
import type { PageEditorState, PageViewport } from './usePageEditorState.js';

// ── Ajustes de estilo, con alcance por vista ─────────────────────────────────
//
// Lo que resuelve, y por qué no alcanzaba con las utilities del template:
//
//   • Un sitio importado no tiene utilities. El panel de propiedades aparecía
//     vacío entero y no había forma de cambiar ni un tamaño.
//   • Una clase es una sola para todos los anchos. Un párrafo que se ve bien en
//     desktop puede quedar mal en mobile, y arreglarlo rompía el otro.
//
// El alcance NO duplica el selector de ancho del visor (ver Editor Panel Pattern,
// "Exceptions"): dice a qué se aplica lo que se toque, y la vista concreta sale
// de la que se está mirando. Un control que dijera "mobile" mientras el visor
// muestra desktop obligaría a mirar dos lugares para saber qué va a pasar.

/** Alcance del ajuste. `all` = las tres vistas. */
type Scope = 'all' | 'current';

interface Props {
  editor: PageEditorState;
  scope: Scope;
  onScopeChange: (s: Scope) => void;
}

/** La vista a la que va a parar el ajuste, según el alcance elegido. */
export function targetViewport(scope: Scope, viendo: PageViewport): OverrideViewport {
  return scope === 'all' ? 'all' : viendo;
}

function Row({ label, children, puesto }: {
  label: string;
  children: React.ReactNode;
  puesto: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 'var(--evo-space-2)',
      }}>
        <span style={{
          fontSize: 'var(--evo-fs-xs)',
          color: 'var(--evo-fg-3)',
          fontFamily: 'var(--evo-font)',
        }}>
          {label}
        </span>
        {/* Que un valor esté puesto en ESTA vista se dice: si no, un control con
            un número heredado del CSS del sitio parece un ajuste que no existe. */}
        {puesto && (
          <Pill variant="outline" tone="cyan" size="sm">
            {t('projects.editor.style.set')}
          </Pill>
        )}
      </div>
      {children}
    </div>
  );
}

/** Campo de texto libre con unidad (tamaños, márgenes). */
function LengthRow({ editor, prop, vp }: { editor: PageEditorState; prop: OverrideProp; vp: OverrideViewport }) {
  const { t } = useTranslation();
  const value = editor.styleValue(vp, prop);
  return (
    <Row label={t(`projects.editor.style.prop.${prop}`)} puesto={value !== null}>
      {/* Acá NO va el onMouseDown que preventDefault el foco (Editor Panel
          Pattern, regla 5): esa regla es para controles que se aprietan, y en un
          campo hay que poder escribir. La selección del lienzo no se pierde por
          esto — es una referencia al elemento, no la selección de texto del
          navegador, y nada la limpia al cambiar el foco. */}
      <Input
        value={value ?? ''}
        placeholder={t('projects.editor.style.inherited')}
        onChange={v => { editor.setStyleValue(vp, prop, v.trim() === '' ? null : v.trim()); }}
      />
    </Row>
  );
}

function OptionRow({ editor, prop, vp, options }: {
  editor: PageEditorState;
  prop: OverrideProp;
  vp: OverrideViewport;
  options: string[];
}) {
  const { t } = useTranslation();
  const value = editor.styleValue(vp, prop);
  return (
    <Row label={t(`projects.editor.style.prop.${prop}`)} puesto={value !== null}>
      <Select<string>
        value={value}
        onChange={v => { editor.setStyleValue(vp, prop, v); }}
        options={options.map(o => ({ value: o, label: t(`projects.editor.style.value.${o}`) }))}
        placeholder={t('projects.editor.style.inherited')}
        clearable
      />
    </Row>
  );
}

export function StyleSection({ editor, scope, onScopeChange }: Props) {
  const { t } = useTranslation();
  const vp = targetViewport(scope, editor.viewport);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
      <SegmentedControl<Scope>
        value={scope}
        onChange={onScopeChange}
        size="sm"
        options={[
          { value: 'all', label: t('projects.editor.style.scope.all') },
          {
            value: 'current',
            label: t('projects.editor.style.scope.current', {
              view: t(`projects.editor.viewport.${editor.viewport}`),
            }),
          },
        ]}
      />

      <LengthRow editor={editor} prop="fontSize" vp={vp} />
      <LengthRow editor={editor} prop="lineHeight" vp={vp} />
      <OptionRow editor={editor} prop="textAlign" vp={vp} options={['left', 'center', 'right', 'justify']} />
      {/* El "ajuste general" del que hablamos: el navegador reparte las líneas
          en CADA ancho, así que muchas veces evita tener que tocar vista por
          vista. Por eso está antes de los márgenes. */}
      <OptionRow editor={editor} prop="textWrap" vp={vp} options={['balance', 'pretty', 'wrap', 'nowrap']} />
      <LengthRow editor={editor} prop="maxWidth" vp={vp} />
      <LengthRow editor={editor} prop="marginTop" vp={vp} />
      <LengthRow editor={editor} prop="marginBottom" vp={vp} />
      <OptionRow editor={editor} prop="display" vp={vp} options={['none', 'block', 'inline', 'inline-block']} />
    </div>
  );
}
